import { Files } from "../../../modules/financial/models/GetRefundIcmsDTO";

/**
 * Result of matching the refund attachments to their semantic roles.
 * Each role may be undefined when the corresponding attachment is missing.
 */
export interface ResolvedRefundFiles {
  petition?: Files;
  xmlNotes?: Files;
}

/**
 * Returns true when the file looks like the ICMS petition (a PDF document).
 * We inspect the MIME type first and fall back to the file name extension,
 * since the upload flow may send the MIME as a generic value (e.g. zip).
 */
function isPetitionFile(file?: Files): boolean {
  if (!file) return false;
  const fileType = (file.fileType ?? "").toLowerCase();
  const fileName = (file.fileName ?? "").toLowerCase();
  return fileType.includes("pdf") || fileName.endsWith(".pdf");
}

/**
 * Returns true when the file looks like the fiscal notes (an XML document).
 */
function isXmlNotesFile(file?: Files): boolean {
  if (!file) return false;
  const fileType = (file.fileType ?? "").toLowerCase();
  const fileName = (file.fileName ?? "").toLowerCase();
  return fileType.includes("xml") || fileName.endsWith(".xml");
}

/**
 * Maps the raw refund attachments to their semantic roles (petition / notes)
 * instead of trusting the array order.
 *
 * The backend historically returns `[petition, notes]`, but relying on the
 * index breaks silently if the order changes or only one attachment is sent.
 * We first try to identify each file by its type (MIME or extension); when the
 * type is inconclusive we fall back to the original positional mapping so the
 * merge never breaks.
 */
export function resolveRefundFiles(files?: Files[]): ResolvedRefundFiles {
  const list = files ?? [];

  const petitionByType = list.find(isPetitionFile);
  const xmlNotesByType = list.find(isXmlNotesFile);

  // Positional fallback preserves the previous behaviour when the type of an
  // attachment cannot be determined. We only fall back to a positional slot if
  // that file was not already identified as the *other* role by its type, so a
  // lone notes file is never mistaken for the petition (and vice versa).
  let petition = petitionByType;
  if (!petition) {
    const fallback = list[0];
    petition = fallback === xmlNotesByType ? undefined : fallback;
  }

  let xmlNotes = xmlNotesByType;
  if (!xmlNotes) {
    const fallback = list[1] ?? list.find((file) => file !== petition);
    xmlNotes = fallback === petition ? undefined : fallback;
  }

  return { petition, xmlNotes };
}
